// KycFlowPage — Multi-step KYC verification flow
// Steps: 1) Personal info → 2) Address → 3) ID upload → 4) Selfie → 5) Review
// Layout: iOS HIG with progress dots, back chevron, Cancel, then step body

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, CountryFlag, PartnerLogo } from '../../components/Brand';

const STEPS = [
  { key: 'personal',   label: 'Personal'  },
  { key: 'address',    label: 'Address'   },
  { key: 'id',         label: 'ID'        },
  { key: 'selfie',     label: 'Selfie'    },
  { key: 'review',     label: 'Review'    },
] as const;
type StepKey = typeof STEPS[number]['key'];

export function KycFlowPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<StepKey>('personal');
  const [data, setData] = useState({
    firstName: 'Brandon', lastName: 'Lee', dob: '1992-03-15', ssnLast4: '4521', nationality: 'US',
    address: '123 Main Street', apt: 'Apt 4B', city: 'San Francisco', state: 'CA', zip: '94102', country: 'US',
    idType: 'passport' as 'passport' | 'drivers-license' | 'id-card',
    idFront: false, idBack: false, selfie: false,
  });
  const idx = STEPS.findIndex((s) => s.key === step);

  function next() { setStep(STEPS[Math.min(idx + 1, STEPS.length - 1)].key); }
  function back() { if (idx === 0) nav(-1); else setStep(STEPS[idx - 1].key); }

  return (
    <div className="min-h-screen bg-tf-surface-1 flex flex-col">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={back} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Verify identity</div>
        <button onClick={() => { if (confirm('Cancel verification? Your progress will be saved.')) nav(-1); }} data-testid="cancel" className="px-3 text-[15px] text-tf-blue">Cancel</button>
      </div>

      {/* Step indicator */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-tf-text-3">Step {idx + 1} of {STEPS.length}</span>
          <span className="text-[11px] font-semibold text-tf-blue">{STEPS[idx].label}</span>
        </div>
        <div className="flex items-center gap-1" data-testid="kyc-progress">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={['h-1 flex-1 rounded-full transition-colors', i <= idx ? 'bg-tf-blue' : 'bg-[#E5E5EA]'].join(' ')}
              data-testid={`kyc-step-${s.key}`}
            />
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-32">
        {step === 'personal' && <PersonalStep data={data} setData={setData} />}
        {step === 'address' && <AddressStep data={data} setData={setData} />}
        {step === 'id' && <IdStep data={data} setData={setData} />}
        {step === 'selfie' && <SelfieStep data={data} setData={setData} />}
        {step === 'review' && <ReviewStep data={data} />}
      </div>

      {/* Bottom CTA */}
      <div className="fixed inset-x-0 bottom-0 z-10 bg-white/80 backdrop-blur-xl border-t border-tf-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button
          onClick={step === 'review' ? () => alert('Submitted for review. You will be notified within 24 hours.') : next}
          data-testid="kyc-next"
          className="flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98]"
        >
          {step === 'review' ? 'Submit for review' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

function PersonalStep({ data, setData }: { data: any; setData: (d: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Personal information</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">Make sure this matches your government-issued ID exactly.</p>
      </div>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Field label="First name" value={data.firstName} onChange={(v) => setData({ ...data, firstName: v })} testid="kyc-firstname" />
        <Field label="Last name"  value={data.lastName}  onChange={(v) => setData({ ...data, lastName: v })}  testid="kyc-lastname" />
        <Field label="Date of birth" value={data.dob} onChange={(v) => setData({ ...data, dob: v })} type="date" testid="kyc-dob" />
        <Field label="Last 4 of SSN"  value={data.ssnLast4} onChange={(v) => setData({ ...data, ssnLast4: v })} type="number" sub="Used for identity verification only" testid="kyc-ssn" />
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Nationality</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <button onClick={() => setData({ ...data, nationality: 'US' })} className="flex w-full items-center justify-between px-4 py-3 active:bg-tf-surface-1 transition" data-testid="kyc-nationality">
          <div className="flex items-center gap-3">
            <CountryFlag code="US" size={32} />
            <span className="text-[15px] font-semibold text-tf-text-1">United States</span>
          </div>
          {data.nationality === 'US' && (
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
          )}
        </button>
      </div>
    </div>
  );
}

function AddressStep({ data, setData }: { data: any; setData: (d: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Your address</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">We need this to confirm you live in a supported region.</p>
      </div>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Field label="Street address" value={data.address} onChange={(v) => setData({ ...data, address: v })} testid="kyc-address" />
        <Field label="Apt / Unit" value={data.apt} onChange={(v) => setData({ ...data, apt: v })} testid="kyc-apt" />
        <Field label="City" value={data.city} onChange={(v) => setData({ ...data, city: v })} testid="kyc-city" />
        <div className="grid grid-cols-2 border-t border-tf-border">
          <Field label="State" value={data.state} onChange={(v) => setData({ ...data, state: v })} noBorder testid="kyc-state" />
          <Field label="ZIP" value={data.zip} onChange={(v) => setData({ ...data, zip: v })} noBorder testid="kyc-zip" />
        </div>
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Country</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {['US', 'CA', 'GB', 'DE', 'FR', 'AU'].map((c) => (
          <button key={c} onClick={() => setData({ ...data, country: c })} className="flex w-full items-center justify-between border-b border-tf-border last:border-b-0 px-4 py-3 active:bg-tf-surface-1 transition" data-testid={`kyc-country-${c}`}>
            <div className="flex items-center gap-3">
              <CountryFlag code={c} size={32} />
              <span className="text-[15px] font-semibold text-tf-text-1">{c === 'US' ? 'United States' : c === 'CA' ? 'Canada' : c === 'GB' ? 'United Kingdom' : c === 'DE' ? 'Germany' : c === 'FR' ? 'France' : 'Australia'}</span>
            </div>
            {data.country === c && (
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function IdStep({ data, setData }: { data: any; setData: (d: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Government-issued ID</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">Take a clear photo. Make sure all 4 corners are visible.</p>
      </div>

      {/* ID type */}
      <h3 className="px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">ID type</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {[
          { key: 'passport', label: 'Passport', sub: 'Most countries' },
          { key: 'drivers-license', label: "Driver's license", sub: 'US, CA, GB' },
          { key: 'id-card', label: 'National ID card', sub: 'EU, Asia' },
        ].map((t, i) => (
          <button
            key={t.key}
            onClick={() => setData({ ...data, idType: t.key })}
            data-testid={`kyc-idtype-${t.key}`}
            className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < 2 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue-tint text-[18px]">
              {t.key === 'passport' ? '🛂' : t.key === 'drivers-license' ? '🚗' : '🪪'}
            </div>
            <div className="flex-1 text-left">
              <div className="text-[14px] font-semibold text-tf-text-1">{t.label}</div>
              <div className="text-[11px] text-tf-text-3">{t.sub}</div>
            </div>
            {data.idType === t.key && (
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
            )}
          </button>
        ))}
      </div>

      {/* Uploads */}
      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Photos</h3>
      <div className="grid grid-cols-2 gap-2.5">
        <UploadTile label="Front" uploaded={data.idFront} onClick={() => setData({ ...data, idFront: !data.idFront })} testid="kyc-id-front" />
        {data.idType !== 'passport' && <UploadTile label="Back" uploaded={data.idBack} onClick={() => setData({ ...data, idBack: !data.idBack })} testid="kyc-id-back" />}
      </div>
      <p className="text-[11px] text-tf-text-3 text-center px-4">
        Photos are encrypted with AES-256 and used only for verification. Powered by <PartnerLogo name="persona" height={14} className="inline-block align-middle" />
      </p>
    </div>
  );
}

function SelfieStep({ data, setData }: { data: any; setData: (d: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[22px] font-bold tracking-tight text-tf-text-1">Selfie verification</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">Look directly at the camera. Make sure your face is well-lit.</p>
      </div>

      <div className="mx-auto w-64 rounded-[24px] border-2 border-dashed border-tf-blue bg-tf-blue-tint p-8 flex flex-col items-center" style={{ background: 'linear-gradient(180deg, rgba(0,122,255,0.05) 0%, rgba(88,86,214,0.05) 100%)' }}>
        <div className="relative mb-3">
          <UserAvatar size={120} name="selfie" />
          <div className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-tf-profit text-white text-[20px]">
            {data.selfie ? '✓' : '📷'}
          </div>
        </div>
        <button
          onClick={() => setData({ ...data, selfie: !data.selfie })}
          data-testid="kyc-selfie"
          className="mt-2 h-11 px-6 rounded-full bg-tf-blue text-white text-[14px] font-semibold active:opacity-40"
        >
          {data.selfie ? 'Retake selfie' : 'Take selfie'}
        </button>
        <p className="mt-2 text-[10px] text-tf-text-3 text-center">Face must match your ID</p>
      </div>

      <div className="rounded-[14px] border border-tf-blue/30 bg-tf-blue-tint p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">💡</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-blue">Liveness check</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">We'll ask you to blink and turn your head. This prevents identity fraud.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ data }: { data: any }) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-tf-green-tint text-tf-profit text-[36px]">✓</div>
        <h2 className="mt-3 text-[22px] font-bold tracking-tight text-tf-text-1">Review & submit</h2>
        <p className="mt-1 text-[14px] text-tf-text-3">Make sure everything is correct. You can edit after submission if needed.</p>
      </div>

      <h3 className="px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Personal</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <ReviewRow label="Name"        value={`${data.firstName} ${data.lastName}`} />
        <ReviewRow label="Date of birth" value={data.dob} />
        <ReviewRow label="Nationality" value="United States" />
        <ReviewRow label="SSN (last 4)" value="••••4521" />
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">Address</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <ReviewRow label="Address" value={`${data.address}, ${data.apt}`} />
        <ReviewRow label="City"    value={`${data.city}, ${data.state} ${data.zip}`} />
        <ReviewRow label="Country" value="United States" />
      </div>

      <h3 className="mt-5 px-1 text-[13px] font-semibold uppercase tracking-wide text-tf-text-3">ID</h3>
      <div className="overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <ReviewRow label="Type"    value={data.idType === 'passport' ? 'Passport' : data.idType === 'drivers-license' ? "Driver's License" : 'National ID'} />
        <ReviewRow label="Front"   value={data.idFront ? '✓ Uploaded' : '⚠ Missing'} status={data.idFront ? 'success' : 'warning'} />
        {data.idType !== 'passport' && <ReviewRow label="Back" value={data.idBack ? '✓ Uploaded' : '⚠ Missing'} status={data.idBack ? 'success' : 'warning'} />}
        <ReviewRow label="Selfie"  value={data.selfie ? '✓ Captured' : '⚠ Missing'} status={data.selfie ? 'success' : 'warning'} />
      </div>

      <div className="rounded-[14px] border border-tf-warning/30 bg-tf-orange-tint p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">⏱️</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-warning">Review takes ~24h</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">You'll get a notification once your verification is approved. You can start a challenge right after.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, sub, noBorder, testid }: { label: string; value: string; onChange: (v: string) => void; type?: string; sub?: string; noBorder?: boolean; testid?: string }) {
  return (
    <div className={['px-4 py-2.5', noBorder ? '' : 'border-b border-tf-border'].join(' ')}>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">{label}</label>
      <input
        type={type || 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full bg-transparent text-[15px] font-medium text-tf-text-1 focus:outline-none placeholder:text-tf-text-muted"
      />
      {sub && <div className="text-[10px] text-tf-text-3 mt-0.5">{sub}</div>}
    </div>
  );
}

function ReviewRow({ label, value, status }: { label: string; value: string; status?: 'success' | 'warning' }) {
  return (
    <div className="flex items-center justify-between border-b border-tf-border last:border-b-0 px-4 py-2.5">
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className={['text-[14px] font-medium', status === 'success' ? 'text-tf-profit' : status === 'warning' ? 'text-tf-warning' : 'text-tf-text-1'].join(' ')}>{value}</span>
    </div>
  );
}

function UploadTile({ label, uploaded, onClick, testid }: { label: string; uploaded: boolean; onClick: () => void; testid?: string }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={['flex flex-col items-center justify-center rounded-[16px] border-2 p-6 transition', uploaded ? 'border-tf-profit bg-tf-green-tint' : 'border-dashed border-tf-border bg-white active:scale-95'].join(' ')}
    >
      <div className={['flex h-12 w-12 items-center justify-center rounded-full text-[24px]', uploaded ? 'bg-tf-profit text-white' : 'bg-tf-surface-1'].join(' ')}>
        {uploaded ? '✓' : '📷'}
      </div>
      <div className="mt-2 text-[13px] font-semibold text-tf-text-1">{label}</div>
      <div className="text-[10px] text-tf-text-3">{uploaded ? 'Tap to replace' : 'Tap to upload'}</div>
    </button>
  );
}
